/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type TabId = 'study' | 'chat' | 'library' | 'prayers' | 'profile';

interface NavigationContextValue {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  navigateToChat: () => void;
  navigateToLibrary: () => void;
  navigateToPrayers: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

// Map tab IDs to their default routes
const TAB_ROUTES: Record<TabId, string> = {
  study: '/study',
  chat: '/chat/community',
  library: '/library',
  prayers: '/prayers/healing',
  profile: '/profile',
};

export function NavigationProvider({ children }: NavigationProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive activeTab from current URL path
  const activeTab = useMemo<TabId>(() => {
    const path = location.pathname;
    if (path.startsWith('/chat')) return 'chat';
    if (path.startsWith('/library')) return 'library';
    if (path.startsWith('/prayers')) return 'prayers';
    if (path.startsWith('/profile')) return 'profile';
    if (path.startsWith('/admin')) return 'study'; // Admin routes don't map to a tab
    return 'study';
  }, [location.pathname]);

  // Navigate to a tab using the router - memoized to prevent unnecessary re-renders
  const setActiveTab = useCallback(
    (tab: TabId) => {
      navigate(TAB_ROUTES[tab]);
    },
    [navigate]
  );

  const navigateToChat = useCallback(() => navigate(TAB_ROUTES.chat), [navigate]);
  const navigateToLibrary = useCallback(() => navigate(TAB_ROUTES.library), [navigate]);
  const navigateToPrayers = useCallback(() => navigate(TAB_ROUTES.prayers), [navigate]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      navigateToChat,
      navigateToLibrary,
      navigateToPrayers,
    }),
    [activeTab, setActiveTab, navigateToChat, navigateToLibrary, navigateToPrayers]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
